/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Mobx model related to book Resources
 *
 * @file Resource.js
 * @author JP
 * @since 1.0.0
 */

import { getSnapshot, types } from 'mobx-state-tree';
import EventBus from 'eventing-bus';

import {
  BOOK_LEVEL_RESOURCE,
  BULK_DOWNLOAD_STATUS_COMPLETED,
  BULK_DOWNLOAD_STATUS_ERROR,
  BULK_DOWNLOAD_STATUS_INPROGRESS,
  BULK_DOWNLOAD_STATUS_REMOVING,
  DOWNLOAD_STATUS_DELETING,
  DOWNLOAD_STATUS_ERROR,
  DOWNLOAD_STATUS_INPROGRESS,
  PAGE_LEVEL_RESOURCE,
  RESOURCE_REQUESTED,
  SELECT_ALL
} from '../common/constants';
import ResourceUtils from '../utils/ResourceUtils';

/**
 * A mobx model for resources data
 *
 */
const Resource = types.model(
  'Resource',
  {
    bulkDownloadStatus: types.maybeNull(types.string),
    currentPageId: types.maybeNull(types.array(types.string)),
    filteredContentType: types.maybeNull(types.array(types.frozen())),
    filteredResourceData: types.maybeNull(types.array(types.frozen())),
    filterOn: types.boolean,
    hasDownloadResource: types.boolean,
    isBookResource: types.boolean,
    isCurrentTabEmpty: types.boolean,
    isFetched: types.boolean,
    isNetworkError: types.boolean,
    isPageResource: types.boolean,
    isSearchOn: types.boolean,
    resourceFlatData: types.maybeNull(types.array(types.frozen())),
    resourceLevelTab: types.maybeNull(types.string),
    retryCount: types.number,
    searchedKeyword: types.maybeNull(types.string),
    selectAll: types.boolean,
    selectedResources: types.maybeNull(types.array(types.frozen())),
    structuredResourceData: types.maybeNull(types.array(types.frozen())),
    userRoleTab: types.maybeNull(types.string),
    showTeacherTab: types.boolean,
    showStudentTab: types.boolean
  }

).actions((self) => ({
  /* eslint-disable no-param-reassign */
  /**
   * Fetch resources
   * This method will publish the resource fetch event with GET type
   *
   * @param {*} bookId
   */
  fetch(bookId, userRole) {
    // Publishing the resource request event with method type 'GET'
    EventBus.publish(
      RESOURCE_REQUESTED,
      {
        bookId,
        userRole,
        type: 'GET'
      }
    );
  },

  /**
   * Set the bulk download staus
   *
   * @param {*} status
   */
  setBulkDownloadStatus(status) {
    self.bulkDownloadStatus = status;
  },

  /**
   * Set the filtered content type selected
   *
   * @param {*} filteredContentType
   */
  setFilteredContentType(filteredContentType) {
    self.filteredContentType = filteredContentType;
  },

  /**
   * Set filtered resource data
   *
   * @param {*} filteredResourceData
   */
  setFilteredResourcesData(filteredResourceData) {
    self.filteredResourceData = filteredResourceData;
  },

  /**
   * Sets the filter on/off status
   *
   * @param {*} isFilterOn
   */
  setFilterOn(isFilterOn) {
    self.filterOn = isFilterOn;
  },

  /**
   * Set isCurrentTabEmpty
   *
   * @param {*} isCurrentTabEmpty
   */
  setIsCurrentTabEmpty(isCurrentTabEmpty) {
    self.isCurrentTabEmpty = isCurrentTabEmpty;
  },

  /**
   * Set isPageResource
   *
   * @param {*} isPageResource
   */
  setIsPageResource(isPageResource) {
    self.isPageResource = isPageResource;
  },

  /**
     * Set isPageResource
     *
     * @param {*} isPageResource
     */
  setIsBookResource(isBookResource) {
    self.isBookResource = isBookResource;
  },
  /**
   * Set isFetched
   * TODO: setIsFetched to be renamed
   *
   * @param {boolean} isFetched
   */
  setIsFetched(isFetched) {
    self.isFetched = isFetched;
  },

  /**
   * Set isNetworkError
   *
   * @param {Boolean} isNetworkError
   */
  setIsNetworkError(isNetworkError) {
    self.isNetworkError = isNetworkError;
  },

  /**
   * Sets the search on/off status
   *
   * @param {*} isSearchOn
   */
  setIsSearchOn(isSearchOn) {
    self.isSearchOn = isSearchOn;
  },

  /**
   * set the resources level tab selected
   *
   * @param {*} currentTab
   */
  setResourceLevelTab(currentTab) {
    self.resourceLevelTab = currentTab;
  },

  /**
   * Sets the retry counter values
   *
   * @param {*} retryCount
   */
  setRetryCount(retryCount) {
    self.retryCount = retryCount;
  },

  /**
   * Set the searched key word value
   *
   * @param {*} searchedKeyword
   */
  setSearchedKeyword(searchedKeyword) {
    self.searchedKeyword = searchedKeyword;
  },

  /**
   * Set selected resource data
   *
   * @param {*} filteredResourceData
   */
  setSelectedResources(selectedResourceData, selectAll = false) {
    if (selectAll) {
      if (selectedResourceData === SELECT_ALL) {
        const currentFlatResource = getSnapshot(self.resourceFlatData);
        const resourceLevelTab = self.resourceLevelTab;
        const isFilterOrSearchOn = self.searchedKeyword !== '' || self.filteredContentType.length > 0;
        let selectedResources = [];

        if (isFilterOrSearchOn) {
          const filteredResourceData = getSnapshot(self.filteredResourceData);
          selectedResources = filteredResourceData.filter(resource => ResourceUtils.isResourceDownloadable(resource));
        } else {
          selectedResources = currentFlatResource.filter(resource => ((resourceLevelTab === BOOK_LEVEL_RESOURCE &&
              resource.limitByRoles && resource.limitByRoles.includes(self.userRoleTab))
              || (resourceLevelTab === PAGE_LEVEL_RESOURCE && resource.pageId)) &&
            ResourceUtils.isResourceDownloadable(resource));
        }
        self.selectedResources = selectedResources;
        self.selectAll = true;
      } else {
        self.selectedResources = selectedResourceData;
        self.selectAll = false;
      }
    } else {
      const selectedResources = getSnapshot(self.selectedResources).slice();
      const foundResourceIndex = selectedResources && selectedResources.findIndex(resource => resource.id === selectedResourceData.id);

      if (foundResourceIndex && foundResourceIndex > -1) {
        selectedResources.splice(foundResourceIndex, 1);
      } else {
        selectedResources.push(selectedResourceData);
      }
      self.selectedResources = selectedResources;
      self.selectAll = false;
    }
  },

  /**
   * Set structured resource data
   *
   * @param {*} structuredResources
   */
  setStructuredResourcesData(structuredResources, showDownload, selectedResourcesWitUpdatedStatus) {
    const flatResources = ResourceUtils.constructFlatResource(structuredResources);
    const retryCount = self.retryCount;
    const currentBulkDownloadStatus = self.bulkDownloadStatus;

    self.structuredResourceData = ResourceUtils.constructStructuredResource(structuredResources);
    self.resourceFlatData = flatResources;

    const selectedResources = selectedResourcesWitUpdatedStatus;
    if (showDownload && selectedResources.length) {
      let selectedResourceStatus = selectedResources.map(resource => resource.status);
      selectedResourceStatus = [...new Set(selectedResourceStatus)];

      if (((currentBulkDownloadStatus === BULK_DOWNLOAD_STATUS_REMOVING
          && !selectedResourceStatus.includes(DOWNLOAD_STATUS_DELETING))
        || (currentBulkDownloadStatus !== BULK_DOWNLOAD_STATUS_REMOVING
          && !selectedResourceStatus.includes(DOWNLOAD_STATUS_INPROGRESS)))) {
        const bulkDownloadStatus = selectedResourceStatus.includes(DOWNLOAD_STATUS_ERROR) &&
        retryCount <= 1 ?
          BULK_DOWNLOAD_STATUS_ERROR : BULK_DOWNLOAD_STATUS_COMPLETED;

        this.setBulkDownloadStatus(bulkDownloadStatus);
        if (retryCount > 1) this.setRetryCount(0);

        if (bulkDownloadStatus === BULK_DOWNLOAD_STATUS_ERROR) {
          this.setSelectedResources(selectedResources, true);
        } else {
          this.setSelectedResources([], true);
        }
      } else if (currentBulkDownloadStatus !== BULK_DOWNLOAD_STATUS_INPROGRESS
        && currentBulkDownloadStatus !== BULK_DOWNLOAD_STATUS_REMOVING) {
        this.setBulkDownloadStatus(BULK_DOWNLOAD_STATUS_INPROGRESS);
      }
    }
    self.setIsFetched(true); // TODO: setIsFetched to be renamed
  },

  /**
   * Set user role tab value
   *
   * @param {string} currentTab
   */
  setUserRoleTab(currentTab) {
    self.userRoleTab = currentTab;
  },
  /**
   * Set TeacherTab
   *
   * @param {boolean} teacherTab
   */
  setShowTeacherTab(teacherTab) {
    self.showTeacherTab = teacherTab;
  },
  /**
   * Set StudentTab
   *
   * @param {boolean} studentTab
   */
  setShowStudentTab(studentTab) {
    self.showStudentTab = studentTab;
  }

  /* eslint-enable no-param-reassign */
}));

export default Resource;
